import { ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';

interface FollowUpsProps {
  followUps: string[];
  onSelect: (question: string) => void;
  isLoading: boolean;
}

/** Shorter, warmer chip labels while still submitting the original question. */
function displayLabelForFollowUp(question: string): string {
  const q = question.trim();
  const lower = q.toLowerCase();

  if (/specific.*time|what happened.*when|locations?\b/i.test(q)) {
    return 'Break down times and locations';
  }
  if ((/vehicle|rout|registr|hx\d|fleet/i.test(lower) && /detail|drill|which|focus|specific/i.test(lower)) || /drill into/i.test(lower)) {
    return 'Focus on a vehicle or route';
  }
  if (/anomal|investigate first|priority|what should i/i.test(lower)) {
    return 'See what to look at first';
  }
  if (/telemetry|missing|gps|signal|connectivity/i.test(lower)) {
    return 'Check telemetry gaps';
  }
  if (/delay|late|eta|on[- ]time/i.test(lower)) {
    return 'Dig into delays and ETAs';
  }
  if (/evidence|proof|support/i.test(lower)) {
    return 'Show supporting evidence';
  }
  if (/timeline|sequence|order of events/i.test(lower)) {
    return 'Walk through the timeline';
  }
  if (/risk|at risk|exposure/i.test(lower)) {
    return 'Review delivery risk';
  }
  if (/driver|who drove|shift/i.test(lower)) {
    return 'Look at driver context';
  }

  if (q.length > 72) return `${q.slice(0, 68).trim()}…`;
  return q;
}

export default function FollowUps({ followUps, onSelect, isLoading }: FollowUpsProps) {
  if (followUps.length === 0) return null;

  return (
    <div className="flex items-center gap-2 flex-wrap">
      <span className="text-sm font-medium text-foreground">You could try next:</span>
      {followUps.map((q, i) => (
        <Button
          key={i}
          type="button"
          variant="outline"
          size="sm"
          onClick={() => onSelect(q)}
          disabled={isLoading}
          title={q}
          className="rounded-lg bg-card/60 hover:text-success hover:border-success/60 hover:bg-card max-w-fit-20rem"
        >
          <ArrowRight className="w-3.5 h-3.5 text-success/70 flex-shrink-0" aria-hidden />
          <span className="text-left truncate">{displayLabelForFollowUp(q)}</span>
        </Button>
      ))}
    </div>
  );
}
